import { ReactNode } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { Card } from "../../UI/Card.tsx";
import { SummaryStat } from "../../UI/Chart/SummaryStat.tsx";
import { SectionTitle } from "./SectionTitle.tsx";
import { StudentPlanStatistics } from "./useStudentPlanStatistics.ts";
import { UserStatistics } from "./useUserStatistics.ts";
import { NEW_USERS_COMPARISON_DAYS } from "./newUsersBuckets.ts";

function StatDetail({ children }: { children: ReactNode }) {
  return <span className={"text-xs text-slate-400"}>{children}</span>;
}

function describeChange(current: number, previous: number): string {
  if (previous === 0)
    return `${previous} in the previous ${NEW_USERS_COMPARISON_DAYS} days`;
  const changePercent = Math.round(((current - previous) / previous) * 100);
  const sign = changePercent > 0 ? "+" : "";
  return `${sign}${changePercent}% vs the previous ${NEW_USERS_COMPARISON_DAYS} days (${previous})`;
}

function ExhaustedLessonPackStat({
  studentCount,
  isListOpen,
  onToggleList,
}: {
  studentCount: number | null;
  isListOpen: boolean;
  onToggleList: () => void;
}) {
  const ToggleIcon = isListOpen ? ChevronUpIcon : ChevronDownIcon;
  return (
    <button
      type={"button"}
      onClick={onToggleList}
      disabled={studentCount === null}
      aria-expanded={isListOpen}
      className={
        "-m-2 rounded-lg p-2 text-left transition-colors hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent"
      }
    >
      <SummaryStat
        label={"Active group plans · no lessons left"}
        loading={studentCount === null}
        value={String(studentCount ?? "")}
      >
        <span
          className={
            "flex items-center gap-0.5 text-xs font-medium text-primary"
          }
        >
          {isListOpen ? "Hide list" : "Show list"}
          <ToggleIcon className={"h-4 w-4"} />
        </span>
      </SummaryStat>
    </button>
  );
}

export function UserStatisticsOverview({
  planStatistics,
  userStatistics,
  newUsersInRecentDays,
  isExhaustedLessonPackListOpen,
  onToggleExhaustedLessonPackList,
}: {
  planStatistics: StudentPlanStatistics | null;
  userStatistics: UserStatistics | null;
  newUsersInRecentDays: { lastDays: number; previousDays: number } | null;
  isExhaustedLessonPackListOpen: boolean;
  onToggleExhaustedLessonPackList: () => void;
}) {
  const studentsWithPlanEndingSoonCount = planStatistics
    ? planStatistics.studentsWithGroupPlanEndingSoonCount +
      planStatistics.studentsWithOneOnOnePlanEndingSoonCount
    : null;

  return (
    <Card>
      <SectionTitle>Overview</SectionTitle>
      <div className={"grid grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-6"}>
        <SummaryStat
          label={"Students with an active plan"}
          loading={!planStatistics}
          value={
            planStatistics ? String(planStatistics.activePlanStudentCount) : ""
          }
        >
          {planStatistics && (
            <StatDetail>
              {planStatistics.activeGroupPlanStudentCount} group ·{" "}
              {planStatistics.activeOneOnOnePlanStudentCount} 1to1
            </StatDetail>
          )}
        </SummaryStat>
        <SummaryStat
          label={`New users · last ${NEW_USERS_COMPARISON_DAYS} days`}
          loading={!newUsersInRecentDays}
          value={
            newUsersInRecentDays ? String(newUsersInRecentDays.lastDays) : ""
          }
        >
          {newUsersInRecentDays && (
            <StatDetail>
              {describeChange(
                newUsersInRecentDays.lastDays,
                newUsersInRecentDays.previousDays,
              )}
            </StatDetail>
          )}
        </SummaryStat>
        <SummaryStat
          label={"Opened the user area · last 7 days"}
          loading={!userStatistics}
          value={
            userStatistics ? String(userStatistics.loggedInLast7DaysCount) : ""
          }
        >
          {userStatistics && (
            <StatDetail>
              {userStatistics.loggedInLast30DaysCount} in the last 30 days
            </StatDetail>
          )}
        </SummaryStat>
        <SummaryStat
          label={
            planStatistics
              ? `Plans ending · next ${planStatistics.planEndingWithinDays} days`
              : "Plans ending soon"
          }
          loading={studentsWithPlanEndingSoonCount === null}
          value={String(studentsWithPlanEndingSoonCount ?? "")}
        >
          {planStatistics && (
            <StatDetail>
              {planStatistics.studentsWithGroupPlanEndingSoonCount} group ·{" "}
              {planStatistics.studentsWithOneOnOnePlanEndingSoonCount} 1to1 ·
              not renewed yet
            </StatDetail>
          )}
        </SummaryStat>
        <ExhaustedLessonPackStat
          studentCount={
            planStatistics
              ? planStatistics.studentsWithExhaustedLessonPack.length
              : null
          }
          isListOpen={isExhaustedLessonPackListOpen}
          onToggleList={onToggleExhaustedLessonPackList}
        />
      </div>
    </Card>
  );
}
